import React, { useRef, useEffect } from 'react';
import { useLocalization } from '../hooks/useLocalization';
import { PriceBandDate } from '../services/tourvisioService';

interface PriceBandProps {
    currentDate: string;
    alternatives: PriceBandDate[];
    onDateSelect: (date: string) => void;
    isLoading: boolean;
}

export const PriceBand: React.FC<PriceBandProps> = ({ currentDate, alternatives, onDateSelect, isLoading }) => {
    const { language } = useLocalization();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Get the base date from string
    const baseDateStr = currentDate.includes('T') ? currentDate.split('T')[0] : currentDate;
    const baseDate = new Date(baseDateStr);
    
    // Create a 7 day window [-3, +3] based around the current date to guarantee it displays even if cache miss
    const displayDates = [];
    for (let i = -3; i <= 3; i++) {
        const d = new Date(baseDate);
        d.setDate(baseDate.getDate() + i);
        
        // Don't show past dates relative to today
        const today = new Date();
        today.setHours(0,0,0,0);
        if (d >= today) {
            displayDates.push(d.toISOString().split('T')[0]);
        }
    }

    // Scroll to center the checked item on load
    useEffect(() => {
        if (scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const selectedItem = container.querySelector('.selected-date') as HTMLElement;
            if (selectedItem) {
                const scrollLeft = selectedItem.offsetLeft - (container.clientWidth / 2) + (selectedItem.clientWidth / 2);
                container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
            }
        }
    }, [alternatives]);

    if (!isLoading && alternatives.length === 0) {
        return null;
    }

    const formatShortDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString(language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : 'en-GB', { 
            weekday: 'short', 
            day: 'numeric', 
            month: 'short' 
        });
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6 relative">
            {isLoading && (
                <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center pointer-events-none rounded-xl">
                    <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
            )}
            <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                 <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {language === 'sv' ? 'Alternativa datum' : language === 'da' ? 'Alternative datoer' : 'Alternative Dates'}
                </h3>
            </div>
            
            <div 
                ref={scrollContainerRef}
                className="flex overflow-x-auto hide-scrollbar snap-x snap-mandatory py-4 px-2"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {displayDates.map(dateStr => {
                    const isSelected = dateStr === baseDateStr;
                    const cachedData = alternatives.find(a => a.date === dateStr);
                    
                    return (
                        <button
                            key={dateStr}
                            onClick={() => !isSelected && onDateSelect(dateStr)}
                            className={`
                                flex-shrink-0 w-28 mx-2 rounded-xl border p-3 flex flex-col items-center justify-center transition-all duration-200 snap-center
                                ${isSelected 
                                    ? 'selected-date border-amber-500 bg-amber-50 shadow-md transform pointer-events-none' 
                                    : 'border-gray-200 hover:border-amber-300 hover:bg-orange-50/50 hover:shadow transform hover:-translate-y-1 cursor-pointer'
                                }
                            `}
                        >
                            <span className={`text-xs font-bold uppercase ${isSelected ? 'text-amber-700' : 'text-gray-500'}`}>
                                {formatShortDate(dateStr)}
                            </span>
                            
                            <div className="mt-2 h-6 flex items-center justify-center">
                                {cachedData ? (
                                    <span className={`text-sm font-black ${isSelected ? 'text-amber-600' : 'text-gray-900'}`}>
                                        {Math.round(cachedData.price).toLocaleString()} {cachedData.currency}
                                    </span>
                                ) : (
                                    <span className="text-xs text-gray-400 font-medium italic">
                                        {language === 'sv' ? 'Sök pris' : language === 'da' ? 'Søg pris' : 'Check price'}
                                    </span>
                                )}
                            </div>
                        </button>
                    )
                })}
            </div>
        </div>
    );
};
